import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useDashboardPreferences } from "../hooks/useDashboardPreferences";
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, AlertTriangle,
  Package, Loader2, ClipboardList, ArrowRight, LayoutGrid, Check,
  GripVertical, Receipt, Star, Award, BarChart2,
  Pencil, Sparkles,
} from "lucide-react";
import { formatCurrency } from "../lib/utils";
import api from "../lib/api";
import { OnboardingChecklist } from "../components/OnboardingManager";  // legacy — kept as fallback during Fase 1 rollout
import HeroKpi from "../components/dashboard/HeroKpi";
import InsightRail from "../components/dashboard/InsightRail";
import FloatingOnboarding from "../components/dashboard/FloatingOnboarding";
import WelcomeSuppliersBanner from "../components/dashboard/WelcomeSuppliersBanner";
import ExpiringProductionWidget from "../components/dashboard/ExpiringProductionWidget";
import DailyClosingStatusWidget from "../components/dashboard/DailyClosingStatusWidget";
import FoodCostKPI from "../components/dashboard/FoodCostKPI";
import CostAlertsCard from "../components/dashboard/CostAlertsCard";
import WeeklyBrief from "../components/dashboard/WeeklyBrief";
import AutoRefillNudge from "../components/dashboard/AutoRefillNudge";
// DashboardAlertsStack was merged into InsightRail (Feb 2026) — alerts now
// render as severity-coloured cards in the rail, so there's a single
// attention zone instead of competing strips and full-width banners.
// AICreditsCard moved to Subscription page (merged with "Historial de consumo")
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from "recharts";

const ResponsiveGridLayout = WidthProvider(Responsive);

// Icon map for backend-defined dashboard templates.
const TEMPLATE_ICONS = { LayoutGrid, TrendingUp, Package, BarChart3: BarChart2 };

// ─── Date preset helpers ──────────────────────────────────────────────────────
function getPresetDates(preset) {
  const now = new Date();
  // Use local date parts to avoid UTC offset causing wrong date (e.g. UTC+1 → yesterday)
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today": return { start: fmt(today), end: fmt(today) };
    case "week": {
      const mon = new Date(today);
      mon.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
      return { start: fmt(mon), end: fmt(today) };
    }
    case "month": return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
    case "last_month": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: fmt(first), end: fmt(last) };
    }
    case "year":      return { start: fmt(new Date(today.getFullYear(), 0, 1)), end: fmt(today) };
    case "year_prev": return { start: fmt(new Date(today.getFullYear() - 1, 0, 1)), end: fmt(new Date(today.getFullYear() - 1, 11, 31)) };
    default: return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) };
  }
}

// ─── KPI definitions ──────────────────────────────────────────────────────────
// `route` enables deep-link navigation when the card is clicked. Each KPI
// drills into the screen where the user can dig deeper into that metric.
const ALL_KPIS = [
  { id: "today",        label: "Ventas hoy",        icon: DollarSign,    color: "green",  spark: "sales",  route: "/app/analytics?view=overview&range=today" },
  { id: "week",         label: "Ventas semana",      icon: ShoppingCart,  color: "blue",   spark: "sales",  route: "/app/analytics?view=overview&range=week" },
  { id: "month",        label: "Ventas mes",         icon: TrendingUp,    color: "orange", spark: "sales",  route: "/app/analytics?view=overview&range=month" },
  { id: "period",       label: "Ventas periodo",     icon: BarChart2,     color: "purple", spark: "sales",  route: "/app/analytics?view=overview" },
  { id: "avg_ticket",   label: "Ticket medio",       icon: Receipt,       color: "teal",   spark: "ticket", route: "/app/analytics?view=tickets" },
  { id: "avg_margin",   label: "Margen medio",       icon: TrendingUp,    color: "orange", spark: null,     route: "/app/finance?tab=forecast" },
  { id: "customers",    label: "Clientes totales",   icon: Users,         color: "purple", spark: null,     route: "/app/customers" },
  { id: "low_stock",    label: "Alertas stock",      icon: AlertTriangle, color: "yellow", spark: null,     route: "/app/inventory?filter=low_stock" },
  { id: "loyalty_points", label: "Puntos fidelización", icon: Award,      color: "pink",   spark: null,     route: "/app/customers" },
];

// ─── Widget definitions ───────────────────────────────────────────────────────
const ALL_WIDGETS = [
  { id: "sales_chart",       label: "Ventas por día",      icon: BarChart2 },
  { id: "top_products",      label: "Top productos",       icon: Star },
  { id: "bottom_products",   label: "Menos vendidos",      icon: TrendingDown },
  { id: "low_stock",         label: "Alertas de stock",    icon: AlertTriangle },
  { id: "cost_alerts",       label: "Margen estrangulado", icon: TrendingDown },
  { id: "loyalty",           label: "Fidelización",        icon: Users },
  { id: "top_customers",     label: "Top Clientes",        icon: Award },
  { id: "pending_purchases", label: "Pendientes compra",   icon: ClipboardList },
];

const COLOR_MAP = {
  green:  { bg: "bg-green-100 dark:bg-green-900/30",   text: "text-green-600 dark:text-green-400",   spark: "#22c55e" },
  blue:   { bg: "bg-blue-100 dark:bg-blue-900/30",     text: "text-blue-600 dark:text-blue-400",     spark: "#3b82f6" },
  orange: { bg: "bg-primary/10",                       text: "text-primary",                         spark: "hsl(var(--primary))" },
  purple: { bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-600 dark:text-purple-400", spark: "#a855f7" },
  teal:   { bg: "bg-teal-100 dark:bg-teal-900/30",     text: "text-teal-600 dark:text-teal-400",     spark: "#14b8a6" },
  yellow: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-600 dark:text-yellow-400", spark: "#eab308" },
  pink:   { bg: "bg-pink-100 dark:bg-pink-900/30",     text: "text-pink-600 dark:text-pink-400",     spark: "#ec4899" },
};

// ─── Default grid layouts ─────────────────────────────────────────────────────
const DEFAULT_LAYOUTS = {
  lg: [
    { i: "sales_chart",       x: 0, y: 0,  w: 8,  h: 6, minW: 4, minH: 4 },
    { i: "top_products",      x: 8, y: 0,  w: 4,  h: 6, minW: 3, minH: 4 },
    { i: "bottom_products",   x: 0, y: 6,  w: 4,  h: 5, minW: 3, minH: 3 },
    { i: "low_stock",         x: 4, y: 6,  w: 4,  h: 5, minW: 3, minH: 3 },
    { i: "cost_alerts",       x: 8, y: 6,  w: 4,  h: 6, minW: 3, minH: 4 },
    { i: "loyalty",           x: 0, y: 12, w: 4,  h: 5, minW: 3, minH: 3 },
    { i: "top_customers",     x: 4, y: 12, w: 4,  h: 5, minW: 3, minH: 3 },
    { i: "pending_purchases", x: 8, y: 12, w: 4,  h: 5, minW: 3, minH: 3 },
  ],
  md: [
    { i: "sales_chart",       x: 0, y: 0,  w: 10, h: 6, minW: 5, minH: 4 },
    { i: "top_products",      x: 0, y: 6,  w: 5,  h: 6, minW: 3, minH: 4 },
    { i: "bottom_products",   x: 5, y: 6,  w: 5,  h: 6, minW: 3, minH: 3 },
    { i: "low_stock",         x: 0, y: 12, w: 5,  h: 5, minW: 3, minH: 3 },
    { i: "cost_alerts",       x: 5, y: 12, w: 5,  h: 6, minW: 3, minH: 4 },
    { i: "loyalty",           x: 0, y: 18, w: 5,  h: 5, minW: 3, minH: 3 },
    { i: "top_customers",     x: 5, y: 18, w: 5,  h: 5, minW: 3, minH: 3 },
    { i: "pending_purchases", x: 0, y: 23, w: 10, h: 4, minW: 5, minH: 3 },
  ],
};

const STORAGE_KEY_LAYOUT  = "dashboard_layouts_v5";
const STORAGE_KEY_KPIS    = "dashboard_visible_kpis_v5";
const STORAGE_KEY_WIDGETS = "dashboard_visible_widgets_v5";
const DEFAULT_VISIBLE_KPIS    = ["today", "week", "month", "period", "avg_ticket", "avg_margin", "customers", "low_stock"];
const DEFAULT_VISIBLE_WIDGETS = ALL_WIDGETS.map(w => w.id);

// ─── Tiny sparkline component ─────────────────────────────────────────────────
function Sparkline({ data, color }) {
  if (!data?.length) return null;
  const id = `spark-${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={0.35} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="v"
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#${id})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── Custom chart tooltip ─────────────────────────────────────────────────────
const WEEKDAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const formatTooltipLabel = (label, payload) => {
  const raw = payload?.[0]?.payload?.date;
  if (raw && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const d = new Date(raw + "T00:00:00");
    if (!Number.isNaN(d.getTime())) {
      return `${WEEKDAYS_ES[d.getDay()]}, ${label}`;
    }
  }
  return label;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">{formatTooltipLabel(label, payload)}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">
          {p.name}: <span className="font-semibold">
            {typeof p.value === "number" ? formatCurrency(p.value) : p.value}
          </span>
        </p>
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const { currentRestaurant } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [welcomeSuppliersFlag, setWelcomeSuppliersFlag] = useState(
    location.state?.welcomeSuppliers === true
  );

  // Clear the router state so a page refresh doesn't re-trigger the banner
  useEffect(() => {
    if (location.state?.welcomeSuppliers) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.welcomeSuppliers]);

  // One-shot migration (Feb 2026): legacy "X dismiss" buttons used to
  // persist a localStorage flag per day for each widget/alert. After
  // unifying everything under the InsightRail customization popover,
  // those flags became unreachable — users found themselves with a
  // half-empty dashboard and no way to restore it. We wipe the old keys
  // once so the panels reappear, then mark the migration done.
  useEffect(() => {
    try {
      if (localStorage.getItem("mg_dashboard_dismiss_migration_v1") === "done") return;
      const remove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (
          k.startsWith("mg.daily-closing-status.dismissed.") ||
          k.startsWith("mg.food-cost-kpi.dismissed.") ||
          k.startsWith("mg.expiring-production.dismissed.") ||
          k.startsWith("dashboard-alert-dismissed::")
        ) {
          remove.push(k);
        }
      }
      remove.forEach(k => localStorage.removeItem(k));
      localStorage.setItem("mg_dashboard_dismiss_migration_v1", "done");
    } catch { /* localStorage unavailable */ }
  }, []);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pendingLists, setPendingLists] = useState([]);
  const [datePreset, setDatePreset] = useState("month");
  const [customDates, setCustomDates] = useState({ start: "", end: "" });
  const [isCustom, setIsCustom] = useState(false);

  const [editMode, setEditMode] = useState(false);

  // ── Dashboard preferences (server-synced via useDashboardPreferences) ─
  // Layouts (react-grid-layout positions), KPI strip visibility, and
  // widget visibility used to live in plain localStorage with global
  // keys ("dashboard_layouts_v5", "dashboard_visible_kpis_v5"...) — that
  // meant the same layout applied to ALL the user's restaurants and
  // never followed them across devices. Now they live in
  // `user_dashboard_preferences` per (user × restaurant), with a thin
  // localStorage cache for instant paint. The migration in
  // `useEffect` below pushes any pre-existing global localStorage state
  // to the backend once and then deletes the keys.
  const restaurantId = currentRestaurant?.restaurant_id;
  const { preferences: dashPrefs, setPreference, applyTemplate, loading: prefsLoading } = useDashboardPreferences(restaurantId);

  const layouts       = dashPrefs.widget_layouts  || DEFAULT_LAYOUTS;
  const visibleKpis   = dashPrefs.visible_kpis    || DEFAULT_VISIBLE_KPIS;
  const visibleWidgets= dashPrefs.visible_widgets || DEFAULT_VISIBLE_WIDGETS;
  const appliedTemplate = dashPrefs.applied_template || null;

  // Curated dashboard templates catalog (shared backend source of truth).
  const [templates, setTemplates] = useState([]);
  const activeTemplate = appliedTemplate ? templates.find(t => t.id === appliedTemplate) : null;
  useEffect(() => {
    let cancelled = false;
    api.request("/dashboard-templates")
      .then((res) => { if (!cancelled) setTemplates(res?.templates || []); })
      .catch(() => { /* picker simply won't render */ });
    return () => { cancelled = true; };
  }, []);

  // One-shot migration of legacy global localStorage to per-restaurant
  // server prefs. Waits for the backend GET to complete (`prefsLoading`)
  // so the migration writes don't get overwritten by the in-flight GET
  // response. Runs once per restaurant.
  useEffect(() => {
    if (!restaurantId || prefsLoading) return;
    const migKey = `mg_dashboard_kpi_widgets_migration_v1_${restaurantId}`;
    try {
      if (localStorage.getItem(migKey) === "done") return;
      const legacyLayout   = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT)  || "null");
      const legacyKpis     = JSON.parse(localStorage.getItem(STORAGE_KEY_KPIS)    || "null");
      const legacyWidgets  = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS) || "null");
      // Push to backend only if there's actually something to migrate AND
      // the backend doesn't already have a value (don't trample a value
      // the user may have set on another device).
      if (legacyLayout   && !dashPrefs.widget_layouts)  setPreference("widget_layouts",  legacyLayout);
      if (legacyKpis     && !dashPrefs.visible_kpis)    setPreference("visible_kpis",    legacyKpis);
      if (legacyWidgets  && !dashPrefs.visible_widgets) setPreference("visible_widgets", legacyWidgets);
      // Mark done so we don't keep recomputing on every render.
      localStorage.setItem(migKey, "done");
      // The legacy global keys are now per-restaurant in the backend.
      // Keep the localStorage as a no-op for other restaurants that
      // still need to migrate.
    } catch { /* ignore quota / parse errors */ }
  }, [restaurantId, prefsLoading, dashPrefs.widget_layouts, dashPrefs.visible_kpis, dashPrefs.visible_widgets, setPreference]);

  const [drillDown, setDrillDown] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchData = useCallback(async (preset, custom) => {
    if (!currentRestaurant) return;
    setLoading(true);
    try {
      const dates = isCustom || preset === "custom" ? custom : getPresetDates(preset);
      const [dashData, lists] = await Promise.all([
        api.getDashboard(currentRestaurant.restaurant_id, dates.start, dates.end),
        api.getShoppingLists(currentRestaurant.restaurant_id, "pending").catch(() => []),
      ]);
      setData(dashData);
      setPendingLists(lists || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant, isCustom]);

  useEffect(() => { fetchData(datePreset, customDates); }, [currentRestaurant, datePreset, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending items aggregation
  const pendingItemsMap = {};
  pendingLists.forEach(list => {
    (list.items || []).forEach(item => {
      const key = (item.ingredient_name || "").toLowerCase();
      if (!key) return;
      if (pendingItemsMap[key]) {
        pendingItemsMap[key].quantity += item.quantity || 0;
        pendingItemsMap[key].estimated_cost += item.estimated_cost || 0;
      } else {
        pendingItemsMap[key] = { ingredient_name: item.ingredient_name, quantity: item.quantity || 0, unit: item.unit || "", estimated_cost: item.estimated_cost || 0 };
      }
    });
  });
  const pendingItems = Object.values(pendingItemsMap).sort((a, b) => b.estimated_cost - a.estimated_cost);
  const pendingTotal = pendingItems.reduce((s, i) => s + i.estimated_cost, 0);

  const handleLayoutChange = (_, allLayouts) => {
    setPreference("widget_layouts", allLayouts);
  };

  const toggleKpi = (id) => {
    const next = visibleKpis.includes(id) ? visibleKpis.filter(k => k !== id) : [...visibleKpis, id];
    setPreference("visible_kpis", next);
    if (appliedTemplate) setPreference("applied_template", null);
  };

  const toggleWidget = (id) => {
    const next = visibleWidgets.includes(id) ? visibleWidgets.filter(w => w !== id) : [...visibleWidgets, id];
    setPreference("visible_widgets", next);
    if (appliedTemplate) setPreference("applied_template", null);
  };

  const resetLayout = () => {
    // Reset all three prefs to "use defaults" (null on backend = client
    // falls back to DEFAULT_LAYOUTS / DEFAULT_VISIBLE_KPIS / WIDGETS).
    setPreference("widget_layouts", null);
    setPreference("visible_kpis", null);
    setPreference("visible_widgets", null);
  };

  const handleBarClick = async (payload) => {
    if (!payload?.activePayload?.length) return;
    const date = payload.activeLabel;
    setDrillLoading(true);
    setDrillDown({ type: "day", title: `Ventas del ${date}`, data: null });
    try {
      const detail = await api.getDaySalesDetail(currentRestaurant.restaurant_id, date);
      setDrillDown({ type: "day", title: `Detalle de ventas — ${date}`, data: detail });
    } catch (e) {
      setDrillDown(null);
    } finally {
      setDrillLoading(false);
    }
  };

  const handleProductClick = (product, type) => {
    setDrillDown({ type: "product", title: type === "top" ? "Producto destacado" : "Producto menos vendido", data: product });
  };

  const sales = data?.sales || {};
  const topProducts = data?.top_products || [];
  const bottomProducts = data?.bottom_products || [];
  const lowStock = data?.low_stock_alerts || [];
  const loyalty = data?.loyalty || {};
  const topCustomers = data?.loyalty?.top_customers || [];
  const sparklines = data?.sparklines || {};

  const getKpiValue = (id) => {
    switch (id) {
      case "today":         return { value: formatCurrency(sales.today || 0), sub: `${sales.transactions_today || 0} transacciones` };
      case "week":          return { value: formatCurrency(sales.week || 0), sub: `${sales.transactions_week || 0} transacciones` };
      case "month":         return { value: formatCurrency(sales.month || 0), sub: `${sales.transactions_month || 0} transacciones` };
      case "period":        return { value: formatCurrency(sales.period || 0), sub: `${sales.transactions_period || 0} · ${sales.period_trend >= 0 ? "+" : ""}${sales.period_trend || 0}% vs ant.` };
      case "avg_ticket":    return { value: formatCurrency(sales.avg_ticket || 0), sub: "Ticket medio" };
      case "avg_margin":    return { value: `${data?.avg_margin || 0}%`, sub: "Margen medio" };
      case "customers":     return { value: loyalty.total_customers || 0, sub: "Clientes" };
      case "low_stock":     return { value: data?.low_stock_count || 0, sub: "Stock bajo", alert: (data?.low_stock_count || 0) > 0 };
      case "loyalty_points": return { value: loyalty.total_points_issued || 0, sub: "Puntos" };
      default:              return { value: "-", sub: "" };
    }
  };

  const getSparkData = (sparkType) => {
    if (sparkType === "sales")  return sparklines.sales_7d  || [];
    if (sparkType === "ticket") return sparklines.ticket_7d || [];
    return [];
  };

  if (loading && !data) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const chartData = (data?.daily_sales || []).map(d => ({ ...d, name: d.date?.slice(5) || d.date }));
  const visibleKpiDefs = ALL_KPIS.filter(k => visibleKpis.includes(k.id));

  // ─── Widget renderers ─────────────────────────────────────────────────────────
  const renderSalesChart = () => (
    <Card className="border-none shadow-soft h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-base">Ventas por día</CardTitle>
          <button onClick={() => navigate("/app/analytics?view=overview")}
            className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="go-analytics-overview">
            Ver análisis <BarChart2 className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        <div className="h-full min-h-[200px]">
          {chartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4" data-testid="sales-chart-empty">
              <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
                <BarChart2 className="w-5 h-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold mb-1">Aún no hay ventas registradas</h3>
              <p className="text-xs text-muted-foreground mb-4 max-w-[260px]">
                Conecta tu TPV o importa un histórico para ver tu evolución diaria y márgenes.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => navigate("/app/tpv-integration")}
                  className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition"
                  data-testid="sales-chart-empty-connect"
                >
                  Conectar TPV
                </button>
                <button
                  onClick={() => navigate("/app/import")}
                  className="h-9 px-3 rounded-md border border-border text-xs font-medium hover:bg-muted transition"
                  data-testid="sales-chart-empty-import"
                >
                  Importar histórico
                </button>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} onClick={handleBarClick} className="cursor-pointer" margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="salesAreaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="ventas"
                  name="Ventas"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  fill="url(#salesAreaFill)"
                  activeDot={{ r: 5, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 2, cursor: "pointer" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderProductList = (products, title, type) => (
    <Card className="border-none shadow-soft h-full flex flex-col" data-testid={`${type}-products`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {type === "top" ? <Star className="w-4 h-4 text-yellow-500" /> : <TrendingDown className="w-4 h-4 text-muted-foreground" />}
            <CardTitle className="font-heading text-base">{title}</CardTitle>
          </div>
          <button onClick={() => navigate("/app/analytics?view=products")}
            className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="go-analytics-products">
            Ver análisis <BarChart2 className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {products.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">Sin datos de ventas</p>
        ) : (
          <div className="space-y-2">
            {products.map((product, idx) => (
              <button key={product.dish_id} onClick={() => handleProductClick(product, type)}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                data-testid={`${type}-product-${idx}`}>
                <div className={`w-7 h-7 ${type === "top" ? "bg-primary/10" : "bg-muted"} rounded-md flex items-center justify-center font-semibold text-xs ${type === "top" ? "text-primary" : "text-muted-foreground"}`}>{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{product.name}</p>
                  <p className="text-xs text-muted-foreground">{product.quantity} uds</p>
                </div>
                <p className="font-semibold text-sm text-primary">{formatCurrency(product.revenue)}</p>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderLowStock = () => (
    <Card className="border-none shadow-soft h-full flex flex-col" data-testid="low-stock-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Alertas de stock
          </CardTitle>
          <Badge variant="outline" className="text-yellow-800 border-yellow-400 text-xs">{lowStock.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {lowStock.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">Sin alertas de stock</p>
        ) : (
          <div className="space-y-2">
            {lowStock.slice(0, 6).map(item => (
              <button key={item.ingredient_id} onClick={() => navigate("/app/inventory")}
                className="w-full flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/20 transition-colors">
                <div className="flex items-center gap-2 min-w-0">
                  <Package className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                  <div className="text-left min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.current_stock} / {item.min_stock} {item.unit}</p>
                  </div>
                </div>
                <Badge className="bg-yellow-500 text-yellow-950 text-xs flex-shrink-0">Bajo</Badge> {/* WCAG: text-white on yellow-500 ratio 1.91; text-yellow-950 = 8.6 */}
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderLoyalty = () => (
    <Card className="border-none shadow-soft h-full flex flex-col" data-testid="loyalty-widget">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <Users className="w-4 h-4 text-primary" />
          Fidelización
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3 flex-1">
          <div className="text-center p-4 bg-muted/50 rounded-xl flex flex-col justify-center">
            <p className="text-3xl font-bold text-primary">{loyalty.total_customers || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Clientes</p>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-xl flex flex-col justify-center">
            <p className="text-3xl font-bold text-secondary">{loyalty.total_points_issued || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Puntos</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full gap-2 text-primary border-primary/30 hover:bg-primary/5"
          onClick={() => navigate("/app/customers")}>
          <Users className="w-3.5 h-3.5" />Ver clientes<ArrowRight className="w-3.5 h-3.5 ml-auto" />
        </Button>
      </CardContent>
    </Card>
  );

  const renderPendingPurchases = () => (
    <Card className="border-none shadow-soft h-full flex flex-col" data-testid="pending-purchases-widget">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="font-heading flex items-center gap-2 text-base">
          <ClipboardList className="w-4 h-4 text-primary" />
          Pendientes de compra
        </CardTitle>
        <Badge variant="outline" className="text-primary border-primary/30 text-xs">{pendingLists.length} lista{pendingLists.length !== 1 ? "s" : ""}</Badge>
      </CardHeader>
      <CardContent className="flex-1">
        {pendingItems.length === 0 ? (
          <div className="text-center py-6">
            <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
            <p className="text-muted-foreground text-sm">No hay productos pendientes</p>
          </div>
        ) : (
          <div className="space-y-1">
            {pendingItems.slice(0, 5).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-1.5 border-b last:border-0" data-testid={`pending-item-${idx}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{item.ingredient_name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-xs text-muted-foreground">{Number.isInteger(item.quantity) ? item.quantity : item.quantity.toFixed(1)} {item.unit}</span>
                  <span className="text-sm font-semibold text-primary">{formatCurrency(item.estimated_cost)}</span>
                </div>
              </div>
            ))}
            {pendingItems.length > 5 && <p className="text-xs text-muted-foreground pt-1">+{pendingItems.length - 5} más</p>}
            <div className="flex items-center justify-between pt-2 border-t mt-1">
              <span className="text-sm text-muted-foreground">Total estimado</span>
              <span className="font-bold text-primary">{formatCurrency(pendingTotal)}</span>
            </div>
            <Button variant="outline" size="sm" className="w-full mt-2 gap-2 text-primary border-primary/30 hover:bg-primary/5"
              onClick={() => navigate("/app/shopping-lists")} data-testid="go-to-shopping-lists-btn">
              <ClipboardList className="w-3.5 h-3.5" />Ver listas<ArrowRight className="w-3.5 h-3.5 ml-auto" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderTopCustomers = () => (
    <Card className="border-none shadow-soft h-full flex flex-col" data-testid="top-customers-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <Award className="w-4 h-4 text-primary" />
            Top Clientes
          </CardTitle>
          <button
            className="text-xs text-primary hover:underline flex items-center gap-1"
            onClick={() => navigate("/app/customers")}
          >
            Ver todos <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {topCustomers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-6 gap-2">
            <Users className="w-8 h-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground text-center">Sin clientes aún</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topCustomers.map((c, idx) => {
              const medals = ["🥇", "🥈", "🥉"];
              const medal = medals[idx] || `${idx + 1}`;
              return (
                <button
                  key={c.customer_id}
                  onClick={() => navigate("/app/customers")}
                  className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted/50 transition-colors text-left"
                  data-testid={`top-customer-${idx}`}
                >
                  <span className="text-lg w-6 text-center shrink-0">{medal}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.total_visits} visitas · {(c.total_spent || 0).toFixed(0)}€</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.level && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-semibold text-foreground"
                        style={{ background: (c.level.color || "#F97316") + "33" }}
                      >
                        {c.level.name}
                      </span>
                    )}
                    <div className="text-right">
                      <p className="text-sm font-bold text-primary">{(c.points || 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">pts</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const ALL_WIDGET_RENDERERS = {
    sales_chart:       renderSalesChart,
    top_products:      () => renderProductList(topProducts, "Top productos", "top"),
    bottom_products:   () => renderProductList(bottomProducts, "Menos vendidos", "bottom"),
    low_stock:         renderLowStock,
    cost_alerts:       () => <CostAlertsCard restaurantId={currentRestaurant?.restaurant_id} />,
    loyalty:           renderLoyalty,
    top_customers:     renderTopCustomers,
    pending_purchases: renderPendingPurchases,
  };

  // Only render visible widgets, maintaining layout item order
  const activeWidgets = Object.entries(ALL_WIDGET_RENDERERS)
    .filter(([id]) => visibleWidgets.includes(id));

  // Filter layout to only include visible widgets
  const filteredLayouts = {
    lg: (layouts.lg || DEFAULT_LAYOUTS.lg).filter(l => visibleWidgets.includes(l.i)),
    md: (layouts.md || DEFAULT_LAYOUTS.md).filter(l => visibleWidgets.includes(l.i)),
  };

  return (
    <div className="space-y-4" data-testid="dashboard-page">

      {/* ── Top action bar (right-aligned) ───────────────────────────────
              "Editar dashboard" siempre visible aquí. Cuando se activa,
              el modo edición habilita drag/resize sobre los widgets y
              muestra el selector de KPIs/widgets inferior. */}
      <div className="flex items-center justify-between gap-2 -mb-1">
        {/* Active template badge — reminds the user which preset is applied
            and opens edit mode (with the templates picker) on click. */}
        {appliedTemplate && activeTemplate ? (
          <button
            type="button"
            onClick={() => setEditMode(true)}
            data-testid="active-template-badge"
            title={`Plantilla activa: ${activeTemplate.name}. Pulsa para cambiarla.`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 transition-all hover:bg-primary/15 hover:border-primary/40"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="truncate max-w-[160px]">{activeTemplate.name}</span>
          </button>
        ) : <span />}
        <Button
          variant={editMode ? "default" : "ghost"}
          size="sm"
          onClick={() => setEditMode(v => !v)}
          data-testid="toggle-edit-mode-btn"
          className="gap-1.5 h-8 text-xs text-muted-foreground hover:text-foreground"
          aria-label={editMode ? "Guardar cambios del panel" : "Editar panel"}
          title={editMode ? "Guardar cambios" : "Editar panel"}
        >
          {editMode ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{editMode ? "Guardar" : "Editar panel"}</span>
        </Button>
      </div>

      {/* ── Onboarding moved to FloatingOnboarding (bottom-left, Stripe-style).
              Renders at the page root, outside this content flow. */}

      {/* ── Auto-refill nudge — saldo IA bajo + recarga automática desactivada ── */}
      {currentRestaurant?.restaurant_id && (
        <AutoRefillNudge restaurantId={currentRestaurant.restaurant_id} />
      )}

      {/* ── Hero KPI ──────────────────────────────────────────────────────
              Configurable "above-the-fold" metric chosen by the user via the
              gear menu (preference persisted in localStorage). */}
      <HeroKpi
        data={data}
        datePreset={datePreset}
        setDatePreset={setDatePreset}
        isCustom={isCustom}
        setIsCustom={setIsCustom}
        periodTrend={typeof sales.period_trend === "number" ? sales.period_trend : null}
      />

      {/* ── Insight Rail ───────────────────────────────────────────────────
              Horizontal scrollable strip of dynamic insight cards. Auto-hides
              cards whose underlying signal is empty (no clutter). */}
      {currentRestaurant?.restaurant_id && (
        <InsightRail restaurantId={currentRestaurant.restaurant_id} />
      )}

      {/* ── Bento grid — Fase 2 (Feb 2026) ──────────────────────────────────
              Replaces the previous vertical stack of 5 widgets. Two columns
              on desktop / tablet, single column on mobile. Each widget auto-
              hides when its underlying data is empty (handled inside each
              component), so the grid naturally compacts without empty cells.

              Layout:
                Col A: Estado del cierre · Producción caducando
                Col B: Food-cost (CMV teórico) · Brief semanal IA
                Full:  Stack unificada de alertas (variable, mejor a todo ancho)

              Each widget owns its loading state and renders null/skeleton
              while fetching — no artificial parent-level delay. */}
      {currentRestaurant?.restaurant_id && data && (
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-min"
          data-testid="dashboard-bento-section"
        >
          <DailyClosingStatusWidget restaurantId={currentRestaurant.restaurant_id} />
          <FoodCostKPI restaurantId={currentRestaurant.restaurant_id} />
          <ExpiringProductionWidget restaurantId={currentRestaurant.restaurant_id} />
          <WeeklyBrief restaurantId={currentRestaurant.restaurant_id} />
        </div>
      )}

      {/* ── AI Credits panel now lives in /app/subscription → "Historial de consumo" ── */}

      {/* ── Welcome Suppliers Banner (shown once after onboarding) ───────────── */}
      {welcomeSuppliersFlag && currentRestaurant?.restaurant_id && (
        <WelcomeSuppliersBanner
          restaurantId={currentRestaurant.restaurant_id}
          onDismiss={() => setWelcomeSuppliersFlag(false)}
        />
      )}

      {/* ── Header bar ─────────────────────────────────────────────────────────
              Feb 2026: the date-preset selector + custom-range inputs +
              "vs periodo anterior" badge were moved INTO the Hero
              (HeroKpi). The header now only keeps the custom-date inputs
              (when active) and the inline "Restablecer" button while in
              edit mode. "Editar dashboard" lives at the very top of the
              page; "Comunicar al equipo" moved to /app/team. The
              Análisis/Tickets toggle was removed — Tickets ahora viven en
              /app/analytics?view=tickets. */}
      {(isCustom || editMode) && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {isCustom && (
            <div className="flex items-center gap-2">
              <input type="date" value={customDates.start}
                onChange={e => setCustomDates(p => ({ ...p, start: e.target.value }))}
                className="h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid="custom-start-date" />
              <span className="text-muted-foreground text-sm">—</span>
              <input type="date" value={customDates.end}
                onChange={e => setCustomDates(p => ({ ...p, end: e.target.value }))}
                className="h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid="custom-end-date" />
              <Button size="sm" onClick={() => fetchData("custom", customDates)} data-testid="apply-custom-dates-btn">Aplicar</Button>
            </div>
          )}
          {editMode && (
            <Button variant="outline" size="sm" onClick={resetLayout} className="text-xs" data-testid="reset-layout-btn">
              Restablecer
            </Button>
          )}
        </div>
      )}

      {/* ── KPI Strip — static row, outside grid ───────────────────────────────── */}
      <div
        className={`grid gap-3 ${
          visibleKpiDefs.length <= 3 ? "grid-cols-3" :
          visibleKpiDefs.length <= 4 ? "grid-cols-2 sm:grid-cols-4" :
          visibleKpiDefs.length <= 6 ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" :
          "grid-cols-2 sm:grid-cols-4 lg:grid-cols-8"
        }`}
        data-testid="kpi-strip"
      >
        {visibleKpiDefs.map((kpi) => {
          const { value, sub, alert } = getKpiValue(kpi.id);
          const col = COLOR_MAP[kpi.color];
          const sparkData = getSparkData(kpi.spark);
          const hasSpark = kpi.spark && sparkData.length > 0;
          const handleKpiClick = () => {
            if (kpi.route) navigate(kpi.route);
          };
          return (
            <div
              key={kpi.id}
              role="button"
              tabIndex={0}
              onClick={handleKpiClick}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleKpiClick(); } }}
              className="bg-card rounded-xl border border-border p-3 flex flex-col gap-1 min-w-0 cursor-pointer transition-all hover:border-primary/40 hover:shadow-soft hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid={`kpi-${kpi.id}`}
              aria-label={`${kpi.label}: ${value}. Pulsa para ver detalle.`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className={`w-8 h-8 ${col.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <kpi.icon className={`w-4 h-4 ${col.text}`} />
                </div>
                {alert && <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
              </div>
              <p className="text-lg font-bold leading-tight truncate mt-1">{value}</p>
              <p className="text-xs font-medium text-muted-foreground truncate">{kpi.label}</p>
              <p className="text-xs text-muted-foreground truncate leading-tight">{sub}</p>
              {hasSpark && (
                <div className="-mx-1 mt-1">
                  <Sparkline data={sparkData} color={col.spark} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Edit mode panel ─────────────────────────────────────────────────────── */}
      {editMode && (
        <Card className="border-dashed border-primary/40 bg-primary/5" data-testid="kpi-selector-panel">
          <CardContent className="p-4 space-y-4">
            {/* Quick templates */}
            {templates.length > 0 && (
              <div data-testid="dashboard-templates-section">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" /> Plantillas rápidas
                </p>
                <div className="flex flex-wrap gap-2">
                  {templates.map(tpl => {
                    const Icon = TEMPLATE_ICONS[tpl.icon] || LayoutGrid;
                    const active = appliedTemplate === tpl.id;
                    return (
                      <button key={tpl.id} onClick={() => applyTemplate(tpl.id)}
                        data-testid={`apply-template-${tpl.id}`}
                        title={tpl.description}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${active ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-card text-foreground border-border hover:border-primary/60 hover:bg-primary/5"}`}>
                        {active && <Check className="w-3 h-3" />}
                        <Icon className="w-3.5 h-3.5" />
                        {tpl.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Aplica un preset de KPIs y widgets. Puedes ajustarlo manualmente después.
                </p>
              </div>
            )}

            {/* KPI toggles */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">KPIs visibles</p>
              <div className="flex flex-wrap gap-2">
                {ALL_KPIS.map(kpi => {
                  const active = visibleKpis.includes(kpi.id);
                  return (
                    <button key={kpi.id} onClick={() => toggleKpi(kpi.id)}
                      data-testid={`toggle-kpi-${kpi.id}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
                      {active && <Check className="w-3 h-3" />}
                      <kpi.icon className="w-3 h-3" />
                      {kpi.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Widget toggles */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Gráficos y widgets visibles · <span className="normal-case font-normal">Arrastra para reorganizar · Redimensiona desde las esquinas</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_WIDGETS.map(w => {
                  const active = visibleWidgets.includes(w.id);
                  return (
                    <button key={w.id} onClick={() => toggleWidget(w.id)}
                      data-testid={`toggle-widget-${w.id}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-secondary text-secondary-foreground border-secondary" : "bg-card text-muted-foreground border-border hover:border-secondary/50"}`}>
                      {active && <Check className="w-3 h-3" />}
                      <w.icon className="w-3 h-3" />
                      {w.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Responsive Grid (charts only) ───────────────────────────────────────── */}
      {activeWidgets.length > 0 && (
        <div className={editMode ? "ring-1 ring-primary/20 rounded-xl p-2" : ""}>
          <ResponsiveGridLayout
            className="layout"
            layouts={filteredLayouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={60}
            isDraggable={editMode}
            isResizable={editMode}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".drag-handle"
            resizeHandles={["se", "sw", "ne", "nw"]}
            margin={[12, 12]}
          >
            {activeWidgets.map(([id, renderer]) => (
              <div key={id} className="relative" data-testid={`widget-${id}`}>
                {editMode && (
                  <div className="drag-handle absolute top-2 right-2 z-10 w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-primary/20 transition-colors">
                    <GripVertical className="w-3.5 h-3.5 text-primary" />
                  </div>
                )}
                <div className="h-full">{renderer()}</div>
              </div>
            ))}
          </ResponsiveGridLayout>
        </div>
      )}

      {activeWidgets.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <LayoutGrid className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay widgets visibles. Activa alguno en modo edición.</p>
        </div>
      )}

      {/* ── Drill-down modal ─────────────────────────────────────────────────────── */}
      <Dialog open={!!drillDown} onOpenChange={() => setDrillDown(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="drill-down-modal">
          <DialogHeader>
            <DialogTitle>{drillDown?.title}</DialogTitle>
          </DialogHeader>

          {drillLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {!drillLoading && drillDown?.type === "day" && drillDown?.data && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-xl">
                  <p className="text-2xl font-bold text-primary">{formatCurrency(drillDown.data.total)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total ventas</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-xl">
                  <p className="text-2xl font-bold">{drillDown.data.transactions}</p>
                  <p className="text-xs text-muted-foreground mt-1">Transacciones</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-xl">
                  <p className="text-2xl font-bold">{drillDown.data.transactions > 0 ? formatCurrency(drillDown.data.total / drillDown.data.transactions) : "—"}</p>
                  <p className="text-xs text-muted-foreground mt-1">Ticket medio</p>
                </div>
              </div>

              {drillDown.data.hourly?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-3">Ventas por hora</p>
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={drillDown.data.hourly} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="hourlyAreaFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.04} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                        <XAxis dataKey="hora" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="ventas"
                          name="Ventas"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2.5}
                          strokeDasharray="6 4"
                          fill="url(#hourlyAreaFill)"
                          activeDot={{ r: 5, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {drillDown.data.products?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-3">Productos vendidos</p>
                  <div className="space-y-2">
                    {drillDown.data.products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center text-xs font-bold text-primary">{i + 1}</div>
                          <span className="font-medium text-sm">{p.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-muted-foreground">{p.quantity} uds</span>
                          <span className="font-semibold text-sm">{formatCurrency(p.revenue)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {drillDown.data.total === 0 && (
                <p className="text-center text-muted-foreground py-8">No hay ventas registradas para este día</p>
              )}
            </div>
          )}

          {!drillLoading && drillDown?.type === "product" && drillDown?.data && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-lg">{drillDown.data.name}</p>
                  <p className="text-sm text-muted-foreground">Datos del periodo seleccionado</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-xl">
                  <p className="text-3xl font-bold text-primary">{drillDown.data.quantity}</p>
                  <p className="text-sm text-muted-foreground mt-1">Unidades vendidas</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-xl">
                  <p className="text-3xl font-bold">{formatCurrency(drillDown.data.revenue)}</p>
                  <p className="text-sm text-muted-foreground mt-1">Ingresos generados</p>
                </div>
              </div>
              <div className="p-4 border rounded-xl">
                <p className="text-sm text-muted-foreground">
                  Precio medio por unidad: <span className="font-semibold text-foreground">{drillDown.data.quantity > 0 ? formatCurrency(drillDown.data.revenue / drillDown.data.quantity) : "—"}</span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Floating Onboarding (Stripe-style) ─────────────────────────────
              Bottom-left pill, expands into a panel, celebrates + auto-hides
              when all 5 steps are done. Persists state in localStorage. */}
      <FloatingOnboarding />
    </div>
  );
}
